import React, { Key, useEffect, useMemo, useState } from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './InvestorsList.scss'

import iconSearch from '../../../../../Assets/Icon/AdminKycQueue/mna0yq0f-rbvml9l.svg'
import iconFilter from '../../../../../Assets/Icon/AdminInvestors/mna1u63v-ukgkagp.svg'
import iconExport from '../../../../../Assets/Icon/AdminInvestors/mna1u63v-oeyxpsg.svg'
import iconPrev from '../../../../../Assets/Images/AdminInvestors/mna1u63v-h50m4oa.png'
import iconNext from '../../../../../Assets/Icon/AdminInvestors/mna1u63v-bt5ccwl.svg'

import customerService from 'Services/customerService'
import { CustomerType } from 'Models/customerType'
import { HorizontalLoader } from 'Components'

type InvestorFilter = 'all' | 'active' | 'suspended'

const ACCOUNT_PLAN = 'LEASE_INVESTOR'

const InvestorsList: React.FC = () => {
  const { filter } = useParams()
  const activeFilter = filter as InvestorFilter
  const isValidFilter =
    activeFilter === 'all' || activeFilter === 'active' || activeFilter === 'suspended'

  const [pageNumber, setPageNumber] = useState(1)
  const [sortOrder] = useState<'ASC' | 'DESC'>('DESC')

  const [q, setQ] = useState('')
  const [searchQuery, setSearchQuery] = useState<string | null>(null)

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [rows, setRows] = useState<CustomerType[]>([])
  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({
    pageNumber: 1,
    pageSize: 20,
    total: 0,
  })

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  const buildSearchEndpoint = (input: string) => {
    const value = input.trim()
    if (!value) return null

    const encoded = encodeURIComponent(value)
    if (value.includes('@')) return `customerEmailAddress=${encoded}`
    if (/^\+?\d+$/.test(value)) return `customerMsisdn=${encoded}`
    return `customerAccountId=${encoded}`
  }

  useEffect(() => {
    if (!isValidFilter) return

    const fetchCustomers = async () => {
      setLoading(true)
      setErrorMessage(null)
      try {
        const response = searchQuery
          ? await customerService.searchCustomer(searchQuery, pageNumber, sortOrder, ACCOUNT_PLAN)
          : await customerService.index(
              pageNumber,
              sortOrder,
              undefined,
              undefined,
              undefined,
              ACCOUNT_PLAN,
            )

        const data = response?.data?.data
        setRows((data?.dataset ?? []) as CustomerType[])
        setPaginationData({
          pageNumber: Number(data?.pageNumber ?? pageNumber),
          pageSize: Number(data?.pageSize ?? 20),
          total: Number(data?.total ?? 0),
        })
      } catch (e) {
        setRows([])
        setPaginationData({ pageNumber, pageSize: 20, total: 0 })
        setErrorMessage(getErrorMessage(e) ?? 'Failed to load customers')
      } finally {
        setLoading(false)
      }
    }

    fetchCustomers()
  }, [isValidFilter, pageNumber, searchQuery, sortOrder])

  if (!isValidFilter) return <Navigate to='/blockchain/investors/all' replace />

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (activeFilter === 'active' && r.isEnabled !== true) return false
      if (activeFilter === 'suspended' && r.isEnabled !== false) return false
      return true
    })
  }, [activeFilter, rows])

  const totalPages = Math.max(1, Math.ceil(paginationData.total / paginationData.pageSize))

  return (
    <div className='admin-investors'>
      <div className='admin-investors__title'>Investor Management</div>

      <div className='admin-investors__tabs'>
        <Link
          to='/blockchain/investors/all'
          className={[
            'admin-investors__tab',
            activeFilter === 'all' ? 'admin-investors__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          All
        </Link>
        <Link
          to='/blockchain/investors/active'
          className={[
            'admin-investors__tab',
            activeFilter === 'active' ? 'admin-investors__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          Active
        </Link>
        <Link
          to='/blockchain/investors/suspended'
          className={[
            'admin-investors__tab',
            activeFilter === 'suspended' ? 'admin-investors__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          Suspended
        </Link>
      </div>

      <div className='admin-investors__toolbar'>
        <form
          className='admin-investors__search-form'
          onSubmit={(e) => {
            e.preventDefault()
            setPageNumber(1)
            setSearchQuery(buildSearchEndpoint(q))
          }}
        >
          <div className='admin-investors-search'>
            <img src={iconSearch} alt='' className='admin-investors-search__icon' />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className='admin-investors-search__input'
              placeholder='Search by email, msisdn, or accountId'
            />
          </div>

          <button type='submit' className='admin-investors-button'>
            <img src={iconFilter} alt='' className='admin-investors-button__icon' />
            <span>Search</span>
          </button>
        </form>

        <button type='button' className='admin-investors-button'>
          <img src={iconExport} alt='' className='admin-investors-button__icon' />
          <span>Export</span>
        </button>
      </div>

      <div className='admin-investors-table'>
        <div className='table-responsive'>
          <table className='admin-investors-table__native'>
            <thead>
              <tr className='admin-investors-table__head'>
                <th>S/N</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Country</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody className='admin-investors-table__body'>
              {loading && (
                <tr className='admin-investors-table__row'>
                  <td className='admin-investors-table__cell text-center' colSpan={7}>
                    <HorizontalLoader></HorizontalLoader>
                  </td>
                </tr>
              )}

              {!loading && errorMessage && (
                <tr className='admin-investors-table__row'>
                  <td className='admin-investors-table__cell text-center' colSpan={7}>
                    {errorMessage}
                  </td>
                </tr>
              )}

              {!loading && !errorMessage && filteredRows.length === 0 && (
                <tr className='admin-investors-table__row'>
                  <td className='admin-investors-table__cell text-center' colSpan={7}>
                    No customers found
                  </td>
                </tr>
              )}

              {!loading &&
                !errorMessage &&
                filteredRows.map((r, index: Key) => {
                  const status = r.isEnabled ? 'Active' : 'Suspended'
                  return (
                    <tr key={r.accountId} className='admin-investors-table__row'>
                      <td className='admin-investors-table__cell'>
                        {(pageNumber - 1) * paginationData.pageSize + Number(index) + 1}
                      </td>
                      <td className='admin-investors-table__cell'>
                        {r.firstName} {r.lastName}
                      </td>
                      <td className='admin-investors-table__cell'>{r.emailAddress}</td>
                      <td className='admin-investors-table__cell'>{r.msisdn}</td>
                      <td className='admin-investors-table__cell'>{r.country}</td>
                      <td className='admin-investors-table__cell'>
                        <span
                          className={[
                            'admin-investors-table__status',
                            status === 'Active'
                              ? 'admin-investors-table__status--active'
                              : 'admin-investors-table__status--suspended',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {status}
                        </span>
                      </td>
                      <td className='admin-investors-table__cell'>
                        <Link
                          to={`/blockchain/investors/${activeFilter}/${r.accountId}`}
                          className='admin-investors-table__action'
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>

        <div className='admin-investors-pagination'>
          <button
            type='button'
            className='admin-investors-pagination__button'
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1 || loading}
          >
            <img src={iconPrev} alt='' className='admin-investors-pagination__icon' />
            <span>Previous</span>
          </button>
          <div className='admin-investors-pagination__numbers'>
            Page {pageNumber} of {totalPages}
          </div>
          <button
            type='button'
            className='admin-investors-pagination__button'
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
            disabled={pageNumber >= totalPages || loading}
          >
            <span>Next</span>
            <img src={iconNext} alt='' className='admin-investors-pagination__icon' />
          </button>
        </div>
      </div>
    </div>
  )
}

export default InvestorsList
